import { toast } from 'react-toastify';
import { applyDeploymentWording, toDemoWording } from './deploymentWording.utils';

// Attempts to extract a user-friendly message from various payload formats
const extractMessageText = (payload) => {
  if (!payload) return '';

  if (typeof payload === 'string') {
    const text = payload.trim();
    return text.length > 0 ? text : '';
  }

  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const nestedMessage = extractMessageText(entry);
      if (nestedMessage) return nestedMessage;
    }
    return '';
  }

  if (typeof payload === 'object') {
    const directKeys = ['message', 'msg', 'detail', 'error', 'title'];
    for (const key of directKeys) {
      const value = payload?.[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }

    const nestedKeys = ['result', 'data', 'errors'];
    for (const key of nestedKeys) {
      const nestedMessage = extractMessageText(payload?.[key]);
      if (nestedMessage) return nestedMessage;
    }
  }

  return '';
};

/**
 * The message an operator should see for a payload: the text it carries, in the
 * deployment's own wording — a real/demo split is dropped so the caller's
 * fallback speaks instead, and "simulated" reads "demo" (deploymentWording.utils).
 */
export const resolveMessageText = (payload) => applyDeploymentWording(extractMessageText(payload));

const AXIOS_STATUS_MESSAGE_REGEX = /^Request failed with status code \d+$/i;

const resolveErrorMessageText = (errorPayload) => {
  if (!errorPayload) return '';

  if (typeof errorPayload === 'string') {
    const text = resolveMessageText(errorPayload);
    return AXIOS_STATUS_MESSAGE_REGEX.test(text) ? '' : text;
  }

  const candidates = [
    errorPayload?.response?.data,
    errorPayload?.data,
    errorPayload?.error,
    errorPayload?.errors,
    errorPayload?.result,
    errorPayload?.payload,
    errorPayload?.body,
    errorPayload,
  ];

  for (const candidate of candidates) {
    const text = resolveMessageText(candidate);
    if (!text) continue;
    if (AXIOS_STATUS_MESSAGE_REGEX.test(text)) continue;
    return text;
  }

  return '';
};

// Displays the toast
const notify = (type, message, options = {}) => {
  const text = toDemoWording(message)?.trim(); // Removes leading/trailing whitespace
  if (!text) return; // Prevents showing empty toasts

  // Displays the toast with the provided message and configuration options
  toast[type](text, { ...options });
};

/**
 * Displays a standardized success toast notification for add, update, or delete operations.
 *
 * @param {string} objectName - The name of the object or entity being acted upon (e.g., 'Device').
 * @param {Object} [operationFlags] - Flags to indicate the type of operation.
 * @param {boolean} [operationFlags.isAddOperation=true] - If true, indicates an add/create operation.
 * @param {boolean} [operationFlags.isDeleteOperation=false] - If true, indicates a delete operation. If both flags are false, treated as an update operation.
 * @param {Object} [options] - Additional options to pass to the toast (e.g., toastId, position, etc.).
 *
 * @returns {void}
 *
 * Pass `successPayload` in `options` (e.g. an API response) to display the
 * server's returned message instead of the generated one; falls back to the
 * generated message when the payload has none. Mirrors `notifyErrorToast`'s
 * `errorPayload`.
 *
 * @example
 * notifySuccessToast('Device', { isAddOperation: true }); // "Device added successfully."
 * notifySuccessToast('Device', { isDeleteOperation: true }); // "Device deleted successfully."
 * notifySuccessToast('Device', { isAddOperation: false, isDeleteOperation: false }); // "Device updated successfully."
 * notifySuccessToast('Alert', { isAddOperation: true }, { successPayload: apiResponse }); // server message if present
 */
export const notifySuccessToast = (
  objectName,
  { isAddOperation = true, isDeleteOperation = false } = {},
  options,
) => {
  const action = isAddOperation ? 'added' : isDeleteOperation ? 'deleted' : 'updated';
  const fallbackMessage = `${objectName} ${action} successfully.`;
  const { successPayload, ...toastOptions } = options || {};
  const apiMessage = resolveMessageText(successPayload);
  notify('success', apiMessage || fallbackMessage, {
    toastId: toastOptions?.toastId ?? `success:${objectName}:${action}`,
    ...toastOptions,
  });
};

/**
 * Displays a standardized error toast notification for add, update, or delete operations.
 *
 * @param {string} objectName - The name of the object or entity being acted upon (e.g., 'Device').
 * @param {Object} [operationFlags] - Flags to indicate the type of operation.
 * @param {boolean} [operationFlags.isAddOperation=true] - If true, indicates an add/create operation.
 * @param {boolean} [operationFlags.isDeleteOperation=false] - If true, indicates a delete operation. If both flags are false, treated as an update operation.
 * @param {Object} [options] - Additional options to pass to the toast (e.g., toastId, position, etc.).
 *
 * @returns {void}
 *
 * @example
 * notifyErrorToast('Device', { isAddOperation: true }); // "Failed to add Device..."
 * notifyErrorToast('Device', { isDeleteOperation: true }); // "Failed to delete Device..."
 * notifyErrorToast('Device', { isAddOperation: false, isDeleteOperation: false }); // "Failed to update Device..."
 */
export const notifyErrorToast = (
  objectName,
  { isAddOperation = true, isDeleteOperation = false, error } = {},
  options,
) => {
  const action = isAddOperation ? 'add' : isDeleteOperation ? 'delete' : 'update';
  const fallbackMessage = `Failed to ${action} ${objectName}. Please try again, or contact RescAlert Help Desk through the help section within the Settings tab (on the bottom left of your side-bar or by emailing us at level3.support@overwatch.ai`;
  const { errorPayload, ...toastOptions } = options || {};
  const normalizedErrorMessage = resolveErrorMessageText(errorPayload);
  notify('error', normalizedErrorMessage || fallbackMessage, {
    toastId: toastOptions?.toastId ?? `error:${objectName}:${action}`,
    ...toastOptions,
  });
};

/**
 * Starts a persistent loading toast for a long-running flow (e.g. waiting on
 * an SSE). Returns the toast id for progress updates and resolution.
 * Loading toasts intentionally stay pinned and non-dismissible while the
 * work is in flight; resolve them with `resolveLoadingToast`.
 *
 * @param {string} message - Initial progress message.
 * @returns {import('react-toastify').Id|null} Toast id, or null when message is empty.
 */
export const notifyLoadingToast = (message) => {
  const text = typeof message === 'string' ? message.trim() : '';
  if (!text) return null;
  return toast.loading(text);
};

/**
 * Streams a progress message into an active loading toast.
 *
 * @param {import('react-toastify').Id|null} toastId - Id from `notifyLoadingToast`.
 * @param {*} messagePayload - Message text or payload resolvable by `resolveMessageText`.
 */
export const updateLoadingToastProgress = (toastId, messagePayload) => {
  if (toastId === null || toastId === undefined) return;
  const text = resolveMessageText(messagePayload);
  if (!text) return;
  toast.update(toastId, { render: text, isLoading: true });
};

/**
 * Resolves a loading toast into a standard success or error toast.
 *
 * The loading toast is dismissed and the final message is emitted through
 * `notify`, so it inherits the app ToastContainer defaults and behaves
 * exactly like every other toast (persistent until dismissed via the close
 * button, click, or drag). A `toast.update` would instead keep the loading
 * toast's non-dismissible options.
 *
 * @param {import('react-toastify').Id|null} toastId - Id from `notifyLoadingToast`.
 * @param {Object} [resolution]
 * @param {'success'|'error'} [resolution.kind='success'] - Final toast type.
 * @param {*} [resolution.messagePayload] - Payload holding the server/API message.
 * @param {string} [resolution.fallbackMessage=''] - Message when the payload has none.
 */
export const resolveLoadingToast = (
  toastId,
  { kind = 'success', messagePayload, fallbackMessage = '' } = {},
) => {
  if (toastId !== null && toastId !== undefined) toast.dismiss(toastId);
  const resolvedMessage =
    kind === 'error' ? resolveErrorMessageText(messagePayload) : resolveMessageText(messagePayload);
  notify(kind, resolvedMessage || fallbackMessage);
};

export const notifyCommandResultToast = ({
  kind = 'success',
  scope,
  targetId,
  status,
  message,
}) => {
  notify(kind, message, {
    toastId: ['device-command', scope, targetId ?? 'unknown', status ?? 'unknown'].join(':'),
  });
};
